import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
  usePage,
} from '@vibesites/runtime/react';
import { EditableSlotGroup, EditableCollectionBinding } from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
// Parent pages list, shallow-merged with this theme's `src/pages.json`
// overrides (Future Residents Club, Contact position). The runtime doesn't
// expose page metadata (position / parent / template) through a hook, and
// parent-src imports are lint errors. Comfy's package.json doesn't list
// `./pages.json` in its exports map, but the platform's Vite alias resolves
// the bare specifier at build time.
// @ts-ignore — @vibesites/theme-comfy/pages.json is resolved at build time
//              via the Vite alias.
import basePagesData from '@vibesites/theme-comfy/pages.json';
import localPagesData from '../pages.json';
import { VibesitesImage, getMediaUrl, parseStorageKey } from '@vibesites/ui-primitives';
import {
  contrastRatio,
  ensureReadablePair,
  luminanceContrast,
  measureImageLuminance,
  mixColors,
  parseColor,
  pickHighestContrast,
  relativeLuminance,
  resolveCssColor,
} from '../utils/luminance';

// WCAG 1.4.11 non-text contrast minimum for the logo against the scrolled bar.
const LOGO_MIN_CONTRAST = 3;

// ============================================================================
// Dynamic Navigation from pages.json
// ----------------------------------------------------------------------------
// Nav-page filter and header chrome follow the Weidner standard shared by the
// Highland / Homestead / Belmont / Roxanne headers:
//   • Desktop (lg+): a primary-color utility row (phone + address left;
//     Apply Now / Resident Access right) above the logo | nav | Schedule a
//     Tour primary CTA row.
//   • Mobile / tablet: hamburger left, logo centered, call icon (phone) or
//     Schedule a Tour CTA (tablet) right; the drawer slides in from the left
//     with nav, CTAs, Visit Us and Office Hours.
//
//   • `home` is an explicit nav link.
//   • `contact` is the last nav link (Apply Now / Resident Access stay
//     utility / drawer CTAs).
//   • `apply-now` and `resident-access` are excluded from the row.
//   • Three synthetic dropdowns group related pages under a single label:
//       - Find Your Home  → Floor Plans + Interactive Map
//       - Features        → Amenities + Pet Policy + Future Residents Club
//       - Gallery & Tours → Gallery + Virtual Tour + Schedule a Tour
//     Groups collapse to their single surviving child when the property
//     doesn't have the others (e.g. no interactive map).
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

export interface NavLink {
  alias?: string;
  label: string;
  href?: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`, or
      when a synthetic group defines child aliases explicitly. */
  children?: NavLink[];
}

interface NavGroup {
  label: string;
  aliases: string[];
}

const NAV_EXCLUDED_ALIASES = new Set<string>([
  'privacy-policy',
  'sitemap',
  'accessibility',
  // Apply Now + Resident Access are utility-row / drawer CTAs.
  'apply-now',
  'thank-you',
  'resident-access',
  // `schedule-a-tour` participates in the "Gallery & Tours" dropdown.
  'hyperlocal',
  'reviews',
]);

const SYNTHETIC_NAV_GROUPS: NavGroup[] = [
  { label: 'Find Your Home', aliases: ['floor-plans', 'interactive-map'] },
  { label: 'Features', aliases: ['amenities', 'pet-policy', 'future-residents-club'] },
  { label: 'Gallery & Tours', aliases: ['gallery', 'virtual-tour', 'schedule-a-tour'] },
];

const SYNTHETIC_GROUP_ALIASES = new Set(SYNTHETIC_NAV_GROUPS.flatMap((group) => group.aliases));

/** Shallow-merge parent pages with this theme's pages.json overrides so nav
    sees local additions (Future Residents Club) and field tweaks (Contact
    position) the same way the platform page manifest does. */
function mergePagesData(
  base: Record<string, PageEntry>,
  local: Record<string, Partial<PageEntry>>,
): Record<string, PageEntry> {
  const merged: Record<string, PageEntry> = { ...base };
  for (const [alias, override] of Object.entries(local)) {
    merged[alias] = { ...(merged[alias] ?? {}), ...override } as PageEntry;
  }
  return merged;
}

const pagesData = mergePagesData(
  basePagesData as Record<string, PageEntry>,
  localPagesData as Record<string, Partial<PageEntry>>,
);

/** Normalize a path for comparison: drop the query/hash and any trailing
    slash so "/floor-plans/", "/floor-plans?x=1" and "/floor-plans" all match,
    while keeping the root "/" intact. */
function normalizePath(p?: string | null): string {
  if (!p) return '/';
  const path = p.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path || '/';
}

/** A nav link is "active" when its href matches the current page — or, for a
    parent / synthetic group with a dropdown, when any of its children match
    (so the parent stays highlighted while you're on one of its sub-pages). */
function isNavLinkActive(link: NavLink, currentPath: string): boolean {
  const current = normalizePath(currentPath);
  if (link.href && normalizePath(link.href) === current) return true;
  return !!link.children?.some((c) => !!c.href && normalizePath(c.href) === current);
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface MenuGalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
}

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

/** Collapse a week of office hours into consecutive-day runs ("Mon – Fri").
    Days missing from the collection (or with no open/close time) render as
    "Closed" rather than being silently dropped. */
function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = entry.day.trim().toLowerCase().slice(0, 3);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const groups: GroupedHours[] = [];
  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed = !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} – ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.shortLabel.indexOf(' – ');
      const startShort = dash !== -1 ? prev.shortLabel.slice(0, dash) : prev.shortLabel;
      prev.shortLabel = `${startShort} – ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }
  return groups;
}

/**
 * Returns true when the page's primary hero renders the right-edge action rail
 * (home `Hero` or interior `HeroSolidBG`). On those pages the header owns a
 * morphing fixed hamburger on desktop (md+) that overlays the rail's top slot.
 */
// The header is now identical on every page: a persistent horizontal nav on
// lg+ and the hamburger drawer below. No page uses the legacy hero action rail
// / morphing corner hamburger anymore, so this always returns false. (Kept as a
// function so the call site and its useMemo remain unchanged.)
function pageHasHeroRail(_alias: string | undefined, _pathname?: string): boolean {
  return false;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 *
 * Synthetic groups are ANCHORED to the FIRST alias in `aliases` — that
 * alias's page position places the group in the nav row.
 *
 * `hasInteractiveLeasing` gates the Interactive Map page — properties that
 * don't have ApartmentSync *or* Engrain Sightmap config don't get the map
 * page in the nav, and the synthetic Find Your Home dropdown collapses to
 * plain Floor Plans. The page's static `visible` flag is ignored here so
 * the runtime ID check is the sole gate (parent pages.json ships the page
 * as `visible: false`).
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      // Interactive Map visibility is driven solely by leasing/map IDs —
      // skip the static `visible` check so a parent `visible: false`
      // default doesn't permanently hide a configured property.
      if (alias === 'interactive-map') return hasInteractiveLeasing;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];
  const consumedAliases = new Set<string>();

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { alias, label: page.name, href: page.path });
  }

  // Second pass: attach any pages that still use a real pages.json `parent`.
  // Synthetic groups below own their aliases explicitly so they don't inherit
  // parent relationships like `interactive-map -> floor-plans`.
  for (const [alias, page] of eligible) {
    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;
    if (page.parent && SYNTHETIC_GROUP_ALIASES.has(page.parent)) continue;
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    }
  }

  const emittedGroups = new Set<NavGroup>();
  const emitSyntheticGroup = (group: NavGroup) => {
    if (emittedGroups.has(group)) return;
    emittedGroups.add(group);
    const children = group.aliases
      .map((childAlias) => linksByAlias.get(childAlias))
      .filter((link): link is NavLink => !!link);
    for (const child of children) {
      if (child.alias) consumedAliases.add(child.alias);
    }
    if (children.length === 1) {
      topLevel.push(children[0]);
    } else if (children.length > 1) {
      topLevel.push({ label: group.label, children });
    }
  };

  for (const [alias] of eligible) {
    if (consumedAliases.has(alias)) continue;

    const anchoredGroup = SYNTHETIC_NAV_GROUPS.find((item) => item.aliases[0] === alias);
    if (anchoredGroup) {
      emitSyntheticGroup(anchoredGroup);
      continue;
    }

    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;

    const page = linksByAlias.get(alias);
    const parent = (pagesData as Record<string, PageEntry>)[alias]?.parent;
    if (parent && eligibleAliases.has(parent) && parent !== alias) continue;

    if (page) topLevel.push(page);
  }

  for (const group of SYNTHETIC_NAV_GROUPS) {
    emitSyntheticGroup(group);
  }

  return topLevel.filter((link) => !link.children || link.children.length > 0);
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: {
      label: 'Phone Text',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    text: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    text: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function digitsOnly(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/[^+\d]/g, '');
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = digitsOnly(raw);
  return digits ? `tel:${digits}` : raw;
}

function toSmsUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('sms:')) return raw;
  const digits = digitsOnly(raw);
  return digits ? `sms:${digits}` : raw;
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function SmartphoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

function useGalleryRotation(count: number, intervalMs = 5000) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => setActive((i) => (i + 1) % count), intervalMs);
    return () => clearInterval(id);
  }, [count, intervalMs]);

  return active;
}

function MenuGalleryCarousel({ photos }: { photos: { src: string; alt: string }[] }) {
  const active = useGalleryRotation(photos.length);

  if (photos.length === 0) return null;

  return (
    <div className="menu-gallery-carousel" aria-live="polite">
      {photos.map((photo, index) => (
        <div
          key={`${photo.src}-${index}`}
          className={`menu-gallery-carousel__slide${index === active ? ' is-active' : ''}`}
          aria-hidden={index !== active}
        >
          <VibesitesImage
            url={photo.src}
            alt={photo.alt}
            objectFit="cover"
            className="menu-gallery-carousel__image"
          />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isActive,
  isChild,
}: {
  link: NavLink;
  onNavigate: () => void;
  isActive?: boolean;
  /** Nested page — rendered indented under its parent / group heading. */
  isChild?: boolean;
}) {
  // Synthetic group labels have no page of their own. Style them as
  // section headings — not lookalike link rows — so Features / Gallery
  // & Tours / Find Your Home read as grouping labels, not dead taps.
  if (!link.href) {
    return (
      <div
        role="heading"
        aria-level={2}
        className="menu-nav-link menu-nav-group-label block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide"
      >
        {link.label}
      </div>
    );
  }

  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className={`menu-nav-link block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide transition-colors${
        isChild ? ' menu-nav-link--child' : ''
      }${isActive ? ' menu-nav-link--active' : ''}`}
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Desktop Nav Link (horizontal bar, lg+)
// ============================================================================
// A single top-level nav item. Items with children render a CSS-hover
// dropdown (white surface) — see the .nav-more-dropdown rules in the style
// block. The link color is driven by the header state: white when the bar is
// transparent over the hero, theme text color once scrolled/solid.
// Synthetic group labels (no href) render as a button trigger so the label
// opens the dropdown without navigating.

function DesktopNavLink({
  link,
  isActive,
  isNavActive,
}: {
  link: NavLink;
  isActive: boolean;
  isNavActive: (href: string) => boolean;
}) {
  const hasChildren = !!link.children?.length;

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        className={`header-nav-link vantage-nav-darken-hover${isActive ? ' header-nav-link--active' : ''}`}
      >
        {link.label}
      </a>
    );
  }

  const triggerClass = `header-nav-link header-nav-link--parent vantage-nav-darken-hover${isActive ? ' header-nav-link--active' : ''}`;

  return (
    <div className="header-nav-group relative group">
      {link.href ? (
        <a href={link.href} className={triggerClass} aria-haspopup="true">
          <span className="inline-flex items-center gap-1">
            {link.label}
            <ChevronDownIcon className="w-3.5 h-3.5 opacity-70" />
          </span>
        </a>
      ) : (
        <button type="button" className={`${triggerClass} bg-transparent`} aria-haspopup="true">
          <span className="inline-flex items-center gap-1">
            {link.label}
            <ChevronDownIcon className="w-3.5 h-3.5 opacity-70" />
          </span>
        </button>
      )}
      <div className="nav-more-dropdown">
        {link.children!.map((child) => (
          <a
            key={child.href || child.label}
            href={child.href}
            className={`nav-more-dropdown-link${child.href && isNavActive(child.href) ? ' header-nav-link--active' : ''}`}
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE (Weidner standard): utility row (lg+: phone, address | Apply
// Now, Resident Access) above the main row (logo | nav | Schedule a Tour).
// Below lg the main row is hamburger | centered logo | call icon / tour CTA,
// and the utility links fold into the left-hand drawer.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll detection for transparent → solid transition
  useEffect(() => {
    let ticking = false;
    const SCROLL_AT = 20;
    const UNSCROLL_AT = 8;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(prev => {
          if (!prev && y > SCROLL_AT) return true;
          if (prev && y < UNSCROLL_AT) return false;
          return prev;
        });
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the current page synchronously via the runtime context (works at
  // SSG time, no flash-of-white on hydration). Fall back to the URL pathname
  // for any oddball preview/dev path that isn't wired through usePage().
  const pageCtx = usePage();
  // True on pages whose hero renders the action rail (home + interior heroes).
  // A single fixed hamburger morphs between the large rail-tile size at the
  // top of the hero and a compact corner button once scrolled. Below md the
  // rail is hidden, so the standard header hamburger is used instead.
  const heroHasRail = useMemo(
    () =>
      pageHasHeroRail(
        pageCtx?.alias,
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.alias, pageCtx?.path],
  );
  const hideBarActions = heroHasRail;
  // Non-rail pages (home + standard pages) show a persistent horizontal nav in
  // the bar on lg+; rail pages (interior HeroSolidBG) keep the corner rail
  // hamburger instead. The mobile hamburger drawer is used below lg regardless.
  const showDesktopNav = !hideBarActions;

  // Desktop rail pages: one always-mounted fixed hamburger whose size and
  // vertical position interpolate from the hero rail tile to the compact
  // corner button as the page scrolls — no unmount, no entrance animation.
  useEffect(() => {
    if (!heroHasRail) return;

    let raf = 0;
    const MORPH_END = 96;
    let lastTop = -1;
    let lastHeight = -1;
    let lastWidth = -1;

    const update = () => {
      raf = 0;
      if (window.innerWidth < 768) return;

      const hero = document.querySelector('.hero-rail-anchor') as HTMLElement | null;
      if (!hero) return;

      const bannerH = Math.round(
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--offer-banner-height')) || 0,
      );
      const heroRect = hero.getBoundingClientRect();
      const isLg = window.matchMedia('(min-width: 1024px)').matches;
      const compactH = isLg ? 96 : 88;
      const rail = hero.querySelector('.hero-rail') as HTMLElement | null;
      const cssRailWidth = parseFloat(getComputedStyle(hero).getPropertyValue('--hero-rail-width'));
      const expandedW = !Number.isNaN(cssRailWidth) && cssRailWidth > 0
        ? Math.round(cssRailWidth)
        : rail
          ? Math.round(rail.getBoundingClientRect().width)
          : isLg
            ? 220
            : 180;

      // Measure the rail spacer directly — interior heroes keep the home tile
      // size via --hero-rail-width (viewport / 5), not heroHeight / 5. Reading
      // --hero-rail-hamburger-height fails when it is declared as var(--hero-rail-width).
      const spacer = hero.querySelector('.hero-rail-hamburger-spacer') as HTMLElement | null;
      const spacerRect = spacer?.getBoundingClientRect();
      const railH = Math.max(0, heroRect.height - bannerH);
      const slotCount = parseInt(
        getComputedStyle(hero).getPropertyValue('--hero-rail-slot-count') || '5',
        10,
      );
      const tileH =
        spacerRect && spacerRect.height > 0
          ? Math.round(spacerRect.height)
          : !Number.isNaN(cssRailWidth) && cssRailWidth > 0
            ? Math.round(cssRailWidth)
            : Math.round(railH / (slotCount > 0 ? slotCount : 5));

      // Hero-relative scroll — reliable when SPA navigation preserves scrollY.
      const scrollIntoHero = Math.max(0, -heroRect.top);
      const t = Math.min(1, Math.max(0, scrollIntoHero / MORPH_END));

      const railTop = spacerRect?.top ?? heroRect.top + bannerH;
      const compactTop = bannerH;
      const top = Math.round(railTop + (compactTop - railTop) * t);
      const height = Math.round(tileH + (compactH - tileH) * t);
      // Width stays at the square rail tile width — only height/top shrink on scroll.
      const width = expandedW;

      if (top === lastTop && height === lastHeight && width === lastWidth) return;
      lastTop = top;
      lastHeight = height;
      lastWidth = width;

      const root = document.documentElement.style;
      root.setProperty('--rail-hamburger-top', `${top}px`);
      root.setProperty('--rail-hamburger-height', `${height}px`);
      root.setProperty('--rail-hamburger-width', `${width}px`);
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    schedule();
    const mountRetry = requestAnimationFrame(() => {
      requestAnimationFrame(schedule);
    });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    const hero = document.querySelector('.hero-rail-anchor');
    const ro = hero ? new ResizeObserver(schedule) : null;
    if (hero) {
      ro!.observe(hero);
      const spacer = hero.querySelector('.hero-rail-hamburger-spacer');
      if (spacer) ro!.observe(spacer);
    }

    const banner = document.querySelector('.sob');
    const bannerRo = banner ? new ResizeObserver(schedule) : null;
    if (banner) bannerRo!.observe(banner);

    return () => {
      cancelAnimationFrame(mountRetry);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      ro?.disconnect();
      bannerRo?.disconnect();
      document.documentElement.style.removeProperty('--rail-hamburger-top');
      document.documentElement.style.removeProperty('--rail-hamburger-height');
      document.documentElement.style.removeProperty('--rail-hamburger-width');
    };
  }, [heroHasRail, pageCtx?.alias]);

  const tokens = useTokens();

  // CTA accent color for the SCROLLED (white-background) nav state. The
  // outline-style `Schedule a Tour` button paints its border / label in this
  // color. When the property's primary is too pale to read against the white
  // header (contrast < WCAG AA 4.5:1), fall back to the dark `text` token so
  // the CTA stays legible after the transparent → solid header transition.
  // We also expose a paired "on-accent" color for the hover fill state — the
  // runtime keeps `textOnPrimary` calibrated to primary, so when we switch
  // the fill to `text` we have to pair it with `background` (white) instead.
  const [navCtaAccent, navCtaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    return contrastRatio(primary, background) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text)', 'var(--vibe-color-background)'] as const;
  }, [tokens]);

  // Scrolled-state theming based on the property's secondary color. Once the
  // page scrolls past the hero, the header now fills with the secondary token
  // instead of the plain background surface, so the nav / hamburger / logo
  // treatment has to flip based on whether that background is dark or light:
  //
  //   * dark secondary   → keep the mono-white logo + light text (WCAG-safe
  //                        on a dark surface).
  //   * light secondary  → swap in the dark / full-color logo + dark text
  //                        (what the header did on the previous white-bar
  //                        scrolled state).
  //
  // We pick the higher-contrast color out of a small candidate set:
  //   1. the theme's `textOnSecondary` token (calibrated by the runtime),
  //   2. white,
  //   3. the base `text` token (dark).
  // Whichever clears the highest WCAG ratio against the secondary background
  // is exposed via `--vantage-header-scrolled-text`, and an `on-dark` vs
  // `on-light` modifier class drives the logo swap in CSS.
  //
  // Token values the parser can't read (oklch(), var(), named colors) are
  // resolved through the browser after mount. Unparseable colors would
  // otherwise score as luminance 0 (black) and keep the white logo on a
  // light surface.
  const [computedSecondary, setComputedSecondary] = useState<string | null>(null);
  useEffect(() => {
    const raw = tokens?.color?.secondary;
    if (raw && parseColor(raw)) {
      setComputedSecondary(null);
      return;
    }
    const resolved = resolveCssColor(raw || 'var(--vibe-color-secondary)');
    setComputedSecondary(resolved && parseColor(resolved) ? resolved : null);
  }, [tokens]);

  const [scrolledTextColor, scrolledBgIsDark, scrolledBgLuminance] = useMemo(() => {
    const raw = tokens?.color?.secondary;
    const secondary = raw && parseColor(raw) ? raw : computedSecondary;
    if (!secondary) {
      // No readable secondary — fall back to the legacy behavior (dark text
      // on a light surface, dark logo).
      return ['var(--vibe-color-text)', false, 1] as const;
    }
    const white = '#ffffff';
    const textBase = tokens?.color?.text || '#111111';
    const onSecondary = tokens?.color?.textOnSecondary;
    const candidates = [onSecondary, white, textBase].filter(Boolean) as string[];
    const picked = pickHighestContrast(secondary, candidates) || white;
    // Surface reads as dark when white out-contrasts black against it.
    const isDark = contrastRatio(secondary, white) > contrastRatio(secondary, '#000000');
    return [picked, isDark, relativeLuminance(secondary)] as const;
  }, [tokens, computedSecondary]);

  // The utility row paints primary; pick its text color the same way
  // against primary.
  const utilScrolledTextColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return 'var(--vibe-color-text-on-primary)';
    const candidates = [tokens?.color?.textOnPrimary, '#ffffff', tokens?.color?.text || '#111111']
      .filter(Boolean) as string[];
    return pickHighestContrast(primary, candidates) || '#ffffff';
  }, [tokens]);

  // Nav drawer palette. The drawer fills with secondary, its CTAs with
  // primary, and the "Get In Touch" band with a primary/secondary blend —
  // each surface gets its own luminance-picked text color, and a surface is
  // nudged darker/lighter when no candidate reaches WCAG AA (4.5:1).
  const drawerPalette = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    const primary = tokens?.color?.primary;
    const textBase = tokens?.color?.text || '#111111';
    const candidates = (onColor?: string) =>
      [onColor, '#ffffff', textBase].filter(Boolean) as string[];

    const surface = ensureReadablePair(secondary, candidates(tokens?.color?.textOnSecondary));
    const cta = ensureReadablePair(primary, candidates(tokens?.color?.textOnPrimary));
    const band = ensureReadablePair(
      mixColors(primary, secondary, 0.78),
      candidates(tokens?.color?.textOnPrimary),
    );
    const isDark = surface
      ? relativeLuminance(surface.text) > relativeLuminance(surface.background)
      : true;
    return { surface, cta, band, isDark };
  }, [tokens]);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has neither
  // a complete ApartmentSync (Repli360 / RRAC) config nor an Engrain
  // Sightmap ID/URL. ApartmentSync requires BOTH the JavaScript ID
  // (script URL token) and the Config ID (site identifier) — a property
  // with only one populated is a half-configured state and shouldn't
  // show the page (e.g. Centre Point). Engrain is satisfied by any of
  // the sightmap id/url fields used by the `engrain-sitemap` block
  // (including the legacy `sitemap` spellings). ApartmentSync half of
  // this check mirrors parent `ssg-entry.ts`; Engrain is nav-only here
  // until that SSG gate is updated in theme-comfy. The fields are
  // custom property fields (typed as `text`) so we read them via
  // `useProperty()` directly rather than the strongly-typed
  // `usePropertyField` (which only knows the built-in scalar fields).
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const filled = (value: unknown) => !!(value && String(value).trim());
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id;
  const hasApartmentSync = filled(apartmentSyncJsId) && filled(apartmentSyncConfigId);
  const hasEngrain =
    filled(propertyForLeasing?.custom_engrain_sightmap_id) ||
    filled(propertyForLeasing?.custom_engrain_sitemap_id) ||
    filled(propertyForLeasing?.custom_engrain_sightmap_url) ||
    filled(propertyForLeasing?.custom_engrain_sitemap_url);
  const hasInteractiveLeasing = hasApartmentSync || hasEngrain;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  const currentPath = pageCtx?.path
    || (typeof window !== 'undefined' ? window.location.pathname : '/');

  const isNavActive = useCallback((href: string) => {
    const normalized = normalizePath(currentPath);
    const target = normalizePath(href);
    if (target === '/') return normalized === '/';
    return normalized === target || normalized.startsWith(`${target}/`);
  }, [currentPath]);

  // Property featured image — used as the dimmed backdrop behind the
  // slide-in nav drawer (an image sits behind a semi-transparent black wash).
  const menuBgImage = usePropertyField('property_featured_image');
  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const defaultTelUrl = toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const defaultSmsUrl = toSmsUrl(callTrackingPhone) || toSmsUrl(propertyPhone);

  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', { collection: 'office_hours' });
  const groupedHours = useMemo(
    () => (officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : []),
    [officeHours],
  );

  const menuGallery = useCollectionBinding<MenuGalleryPhoto>('header.menu_gallery', {
    collection: 'photo_gallery',
    defaultLimit: 5,
  });
  const menuGalleryPhotos = useMemo(() => {
    // Skip non-photo gallery entries by title, mirroring PhotoGallery — these
    // categories ("Logo", "Other", "Floorplan") aren't real photos and read as
    // noise in the auto-rotating drawer carousel.
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    return (menuGallery || [])
      .filter((photo) => !excludedTitles.has((photo.title || '').toLowerCase().trim()))
      .slice(0, 5)
      .map((photo) => ({
        src: photo.image ?? photo.original_url ?? '',
        alt: photo.alt_text || '',
      }))
      .filter((photo) => photo.src);
  }, [menuGallery]);

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const primaryLogo = usePropertyField('property_primary_logo');
  const headerLogoWhite = monoWhiteLogo || logoRaw.image;
  const headerLogoBlack = primaryLogo || monoBlackLogo || logoRaw.image;
  const headerLogoTop = headerLogoWhite || headerLogoBlack;

  // Scrolled-state logo, chosen by luminance against the secondary bar.
  // Each available logo is scored by the WCAG contrast of its average
  // visible-pixel luminance (sampled on a canvas) against the bar. Until a
  // sample lands — or when CORS blocks it — the mono logos are assumed pure
  // white / black and other assets are trusted. Candidates are tried in
  // preference order (white mark on dark bars, full-color primary on light
  // bars); if none clears 3:1, the best one is recolored to a solid
  // black / white silhouette via CSS filter.
  const scrolledLogoCandidates = useMemo(() => {
    const ordered = scrolledBgIsDark
      ? [monoWhiteLogo, logoRaw.image, primaryLogo, monoBlackLogo]
      : [primaryLogo, monoBlackLogo, logoRaw.image, monoWhiteLogo];
    const urls = ordered.filter((url, i): url is string =>
      typeof url === 'string' && !!url && ordered.indexOf(url) === i,
    );
    return urls.map((url) => ({
      url,
      assumed: url === monoWhiteLogo ? 1 : url === monoBlackLogo ? 0 : null,
    }));
  }, [scrolledBgIsDark, monoWhiteLogo, monoBlackLogo, primaryLogo, logoRaw.image]);

  const [logoLuminance, setLogoLuminance] = useState<Record<string, number | null>>({});
  const logoCandidateKey = scrolledLogoCandidates.map((c) => c.url).join('|');
  useEffect(() => {
    if (!logoCandidateKey) return;
    let cancelled = false;
    Promise.all(
      logoCandidateKey.split('|').map(async (url) => {
        const storageKey = parseStorageKey(url);
        const sampleUrl = storageKey ? getMediaUrl(storageKey, { width: 160 }) : url;
        return [url, await measureImageLuminance(sampleUrl)] as const;
      }),
    ).then((entries) => {
      if (!cancelled) setLogoLuminance(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [logoCandidateKey]);

  const scrolledLogo = useMemo(() => {
    const scored = scrolledLogoCandidates.map((c) => {
      const lum = logoLuminance[c.url] ?? c.assumed;
      return { url: c.url, contrast: lum == null ? null : luminanceContrast(lum, scrolledBgLuminance) };
    });
    if (scored.length === 0) return null;
    const passing = scored.find((s) => s.contrast == null || s.contrast >= LOGO_MIN_CONTRAST);
    if (passing) return { url: passing.url, recolor: false };
    const best = scored.reduce((a, b) => ((b.contrast ?? 0) > (a.contrast ?? 0) ? b : a));
    return { url: best.url, recolor: true };
  }, [scrolledLogoCandidates, logoLuminance, scrolledBgLuminance]);
  const headerLogoHasSwap = !!(headerLogoTop && scrolledLogo && scrolledLogo.url !== headerLogoTop);
  const recolorClass = scrolledLogo?.recolor ? ' header-bar-logo--recolor' : '';

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const phoneLink = useSlotGroup('header.phone_link', phoneLinkDef);
  const textUsLink = useSlotGroup('header.text_us_link', textUsLinkDef);
  const facebookLink = useSlotGroup('header.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('header.instagram_link', instagramLinkDef);

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || defaultTelUrl;
  const textUsHref = toSmsUrl(textUsLink.url) || defaultSmsUrl;
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '(555) 555-5555';
  const menuLogoImage = drawerPalette.isDark ? (monoWhiteLogo || logoRaw.image) : headerLogoBlack;
  const menuLogo = {
    image: menuLogoImage,
    alt: typeof logoRaw.alt === 'string' ? logoRaw.alt : undefined,
  };

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="rubik"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700;800;900&display=swap';
    link.setAttribute('data-vibe-font', 'rubik');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No spacer — transparent header overlays hero content */}

      <header
        className={`fixed left-0 right-0 w-full ${menuOpen ? 'z-[70]' : 'z-50'}${scrolled ? ` header-scrolled ${scrolledBgIsDark ? 'header-scrolled--on-dark' : 'header-scrolled--on-light'}` : ' bg-transparent header-transparent'}${heroHasRail ? ' header-rail-page' : ''}`}
        style={{
          top: 0,
          // Scrolled background is set in CSS (.header-scrolled) rather than
          // inline so a media query can drop it on desktop rail pages, where the
          // pinned rail hamburger replaces the header bar entirely.
          ['--vantage-nav-cta-accent' as string]: navCtaAccent,
          ['--vantage-nav-cta-on-accent' as string]: navCtaOnAccent,
          // Luminance-picked text color for the scrolled state. Consumed by
          // nav links, utility links, hamburger bars and the site-name
          // fallback inside .header-scrolled — see the .header-scrolled
          // overrides in the <style> block below.
          ['--vantage-header-scrolled-text' as string]: scrolledTextColor,
          ['--vantage-util-scrolled-text' as string]: utilScrolledTextColor,
        }}
      >
        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — desktop only (lg+). Phone + address left;      */}
          {/* Apply Now / Resident Access right. Mobile surfaces the same  */}
          {/* links in the drawer.                                         */}
          {/* ============================================================ */}
          <div className="header-util-row hidden lg:block">
            <div className="header-util-row-inner flex items-center justify-between gap-6">
              <div className="flex items-center gap-6 min-w-0">
                <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                  <a
                    href={phoneHref || '#'}
                    target={phoneLink._target || '_self'}
                    className="header-util-link dniphone dniphonehref"
                    aria-label="Call us"
                  >
                    <PhoneIcon className="w-3.5 h-3.5" />
                    <span>{phoneDisplay}</span>
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                  <a
                    href={addressHref}
                    target={addressLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="header-util-link header-util-address"
                    aria-label="View property on Google Maps"
                  >
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{addressLink.text || '123 Main Street, Atlanta, GA 30313'}</span>
                  </a>
                </EditableSlotGroup>
              </div>

              <div className="flex items-center gap-6 flex-shrink-0">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="header-util-link"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="header-util-link"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>

          <div className="header-bar-inner">
            <div className="header-bar-row relative flex items-center">
              {/* Hamburger — mobile / tablet only, pinned left */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden relative w-10 h-10 -ml-2 flex items-center justify-center flex-shrink-0"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-haspopup="true"
              >
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>

              {/* Logo — centered on mobile / tablet, left-aligned on desktop */}
              <EditableSlotGroup
                groupPath="header.logo"
                definition={logoSlotDef}
                className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 lg:static lg:z-auto lg:translate-x-0 lg:translate-y-0 lg:flex lg:flex-shrink-0 lg:items-center"
              >
                <a href="/" className="flex flex-shrink-0 items-center">
                  {headerLogoTop ? (
                    headerLogoHasSwap ? (
                      <span className="header-bar-logo-stack inline-flex items-center">
                        <VibesitesImage
                          url={headerLogoTop}
                          alt={logoRaw.alt || 'Community Logo'}
                          className="header-bar-logo header-bar-logo--light"
                        />
                        <VibesitesImage
                          url={scrolledLogo?.url}
                          alt={logoRaw.alt || 'Community Logo'}
                          className={`header-bar-logo header-bar-logo--dark${recolorClass}`}
                        />
                      </span>
                    ) : (
                      <VibesitesImage
                        url={headerLogoTop}
                        alt={logoRaw.alt || 'Community Logo'}
                        className={`header-bar-logo${recolorClass}`}
                      />
                    )
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Vantage
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop horizontal navigation (lg+, non-rail pages). Below lg
                  and on rail pages the hamburger drawer surfaces these links. */}
              {showDesktopNav && (
                <nav
                  className="header-desktop-nav hidden lg:flex flex-1 items-center justify-center px-6"
                  aria-label="Primary navigation"
                >
                  {navLinks.map((link) => (
                    <DesktopNavLink
                      key={link.href || link.label}
                      link={link}
                      isActive={isNavLinkActive(link, currentPath)}
                      isNavActive={isNavActive}
                    />
                  ))}
                </nav>
              )}

              {/* Right group — call icon on phones; Schedule a Tour primary
                  CTA from tablet up, pinned to the far right of the nav row. */}
              <div className="flex items-center flex-shrink-0 ml-auto lg:ml-0">
                <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef} className="md:hidden">
                  <a
                    href={phoneHref || '#'}
                    target={phoneLink._target || '_self'}
                    className="header-mobile-phone dniphone dniphonehref w-10 h-10 -mr-2 flex items-center justify-center"
                    aria-label="Call us"
                  >
                    <PhoneIcon className="w-5 h-5" />
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef} className="hidden md:flex">
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="header-tour-cta"
                  >
                    {scheduleTourLink.label || 'Schedule a Tour'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop rail-page hamburger — always mounted, morphs between the
            large hero-rail tile and the compact corner button on scroll. */}
        {heroHasRail && (
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="header-rail-hamburger hidden md:flex"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="header-rail-bars" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        )}

        {/* ============================================================== */}
        {/* Menu overlay — sheet on mobile, half-width sheet on tablet,    */}
        {/* full-width on desktop. Backdrop (below lg) dims + dismisses.   */}
        {/* ============================================================== */}
        <div
          aria-hidden={!menuOpen}
          className={`menu-backdrop fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300 lg:hidden ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMobileMenu}
        />

        <div
          aria-hidden={!menuOpen}
          className={`mobile-menu-panel fixed left-0 top-0 bottom-0 w-[calc(100%-3rem)] md:w-1/2 lg:w-full overflow-hidden transition-transform duration-300 ease-out z-[61] flex flex-col ${
            menuOpen ? 'translate-x-0 mobile-menu-open' : '-translate-x-full pointer-events-none'
          }`}
          style={{
            ['--vantage-drawer-bg' as string]: drawerPalette.surface?.background ?? 'var(--vibe-color-secondary)',
            ['--vantage-drawer-text' as string]: drawerPalette.surface?.text ?? 'var(--vibe-color-text-on-secondary)',
            ['--vantage-drawer-cta-bg' as string]: drawerPalette.cta?.background ?? 'var(--vibe-color-primary)',
            ['--vantage-drawer-cta-text' as string]: drawerPalette.cta?.text ?? 'var(--vibe-color-text-on-primary)',
            ['--vantage-drawer-band-bg' as string]: drawerPalette.band?.background ?? 'var(--vibe-color-primary)',
            ['--vantage-drawer-band-text' as string]: drawerPalette.band?.text ?? 'var(--vibe-color-text-on-primary)',
          }}
        >
          {/* Backdrop image — sits behind the black wash. */}
          <div className="menu-bg" aria-hidden="true">
            {menuBgImage ? (
              <VibesitesImage
                url={menuBgImage}
                alt=""
                objectFit="cover"
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
          {/* Black semi-transparent wash over the image. */}
          <div className="menu-overlay" aria-hidden="true" />

          <div className="menu-panel-header relative z-20 flex items-center justify-between px-6 md:px-10 lg:px-14 pt-6 md:pt-8">
            <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
              <a href="/" onClick={closeMobileMenu} className="flex-shrink-0">
                {menuLogo.image ? (
                  <VibesitesImage
                    url={menuLogo.image}
                    alt={menuLogo.alt || 'Community Logo'}
                    className="h-10 md:h-12 w-auto object-contain"
                  />
                ) : (
                  <div className="menu-logo-fallback font-heading text-xl md:text-2xl font-bold">
                    The Vantage
                  </div>
                )}
              </a>
            </EditableSlotGroup>

            <button
              type="button"
              onClick={closeMobileMenu}
              className="menu-close-btn"
              aria-label="Close menu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="menu-scroll relative z-10 flex-1 overflow-y-auto pb-10 md:pb-12">
            <div className="menu-layout flex flex-col md:flex-row md:items-stretch md:min-h-[calc(100%-5.5rem)] px-6 md:px-10 lg:px-14 pt-6 md:pt-10 gap-10 md:gap-0">
              {/* Left column — navigation */}
              <nav role="navigation" aria-label="Main navigation" className="menu-nav md:flex-1 md:max-w-[42%] lg:max-w-[38%]">
                {navLinks.map((link) => (
                  <React.Fragment key={link.href || link.label}>
                    <MobileNavLink
                      link={link}
                      onNavigate={closeMobileMenu}
                      isActive={isNavLinkActive(link, currentPath)}
                    />
                    {link.children?.length ? (
                      <div className="menu-nav-children">
                        {link.children.map((child) => (
                          <MobileNavLink
                            key={child.href || child.label}
                            link={child}
                            onNavigate={closeMobileMenu}
                            isActive={!!child.href && isNavActive(child.href)}
                            isChild
                          />
                        ))}
                      </div>
                    ) : null}
                  </React.Fragment>
                ))}
                <div className="menu-drawer-cta-row">
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      onClick={closeMobileMenu}
                      className={`menu-nav-link menu-drawer-cta block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide transition-colors${
                        isNavActive(residentAccessLink.url || '/resident-access') ? ' menu-nav-link--active' : ''
                      }`}
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      onClick={closeMobileMenu}
                      className={`menu-nav-link menu-drawer-cta block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide transition-colors${
                        isNavActive(applyNowLink.url || '/apply-now') ? ' menu-nav-link--active' : ''
                      }`}
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                    <a
                      href={scheduleTourLink.url || '/schedule-a-tour'}
                      target={scheduleTourLink._target || '_self'}
                      onClick={closeMobileMenu}
                      className={`menu-nav-link menu-drawer-cta block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide transition-colors${
                        isNavActive(scheduleTourLink.url || '/schedule-a-tour') ? ' menu-nav-link--active' : ''
                      }`}
                    >
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </a>
                  </EditableSlotGroup>
                </div>

                <div className="menu-drawer-info">
                  <h3 className="menu-drawer-heading">Visit Us</h3>
                  <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                    <a
                      href={addressHref}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      onClick={closeMobileMenu}
                      className="menu-drawer-info-text menu-drawer-info-link"
                    >
                      {addressLink.text || '123 Main Street, Atlanta, GA 30313'}
                    </a>
                  </EditableSlotGroup>
                </div>

                {groupedHours.length > 0 && (
                  <EditableCollectionBinding bindingPath="header.office_hours" label="Office Hours">
                    <div className="menu-drawer-info">
                      <h3 className="menu-drawer-heading">Office Hours</h3>
                      <dl className="menu-drawer-hours">
                        {groupedHours.map((group, index) => (
                          <React.Fragment key={index}>
                            <dt>{group.shortLabel}</dt>
                            <dd>{group.hours}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </div>
                  </EditableCollectionBinding>
                )}
              </nav>

              {/* Divider */}
              <div className="menu-divider hidden md:block" aria-hidden="true" />

              {/* Right column — contact + gallery */}
              <div className="menu-contact md:flex-1 md:pl-12 lg:pl-16 md:pt-2">
                <div className="space-y-5 md:space-y-6">
                  <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                    <a
                      href={addressHref}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="menu-contact-row group"
                    >
                      <span className="menu-contact-icon-badge flex-shrink-0" aria-hidden="true">
                        <MapPinIcon />
                      </span>
                      <span className="menu-contact-text vantage-nav-darken-hover">
                        {addressLink.text || '123 Main Street, Atlanta, GA 30313'}
                      </span>
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                    <a
                      href={phoneHref || '#'}
                      target={phoneLink._target || '_self'}
                      className="menu-contact-row dniphone dniphonehref group"
                    >
                      <span className="menu-contact-icon-badge flex-shrink-0" aria-hidden="true">
                        <PhoneIcon />
                      </span>
                      <span className="menu-contact-text vantage-nav-darken-hover">
                        {phoneDisplay}
                      </span>
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.text_us_link" definition={textUsLinkDef}>
                    <a
                      href={textUsHref || '#'}
                      target={textUsLink._target || '_self'}
                      className="menu-contact-row group"
                    >
                      <span className="menu-contact-icon-badge flex-shrink-0" aria-hidden="true">
                        <SmartphoneIcon />
                      </span>
                      <span className="menu-contact-text vantage-nav-darken-hover">
                        {textUsLink.text || 'Text Us!'}
                      </span>
                    </a>
                  </EditableSlotGroup>

                  {(facebookLink.url || instagramLink.url) ? (
                    <div className="menu-social flex items-center gap-4 pt-1">
                      {facebookLink.url ? (
                        <EditableSlotGroup groupPath="header.facebook_link" definition={facebookLinkDef}>
                          <a
                            href={facebookLink.url}
                            target={facebookLink._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={facebookLink.text || 'Facebook'}
                            className="menu-social-link"
                          >
                            <FontAwesomeIcon icon={faFacebook} className="w-5 h-5" />
                          </a>
                        </EditableSlotGroup>
                      ) : null}
                      {instagramLink.url ? (
                        <EditableSlotGroup groupPath="header.instagram_link" definition={instagramLinkDef}>
                          <a
                            href={instagramLink.url}
                            target={instagramLink._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={instagramLink.text || 'Instagram'}
                            className="menu-social-link"
                          >
                            <FontAwesomeIcon icon={faInstagram} className="w-5 h-5" />
                          </a>
                        </EditableSlotGroup>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <MenuGalleryCarousel photos={menuGalleryPhotos} />
              </div>
            </div>
          </div>

          {/* Contact footer band — shown at every width now that the mobile
              drawer mirrors the tablet look (the inline contact/gallery column
              is hidden below lg by the drawer-treatment block). Full-width
              strip pinned to the panel base showing a "Get In Touch" phone CTA. */}
          <div className="menu-footer-band block relative z-20">
            <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
              <a
                href={phoneHref || '#'}
                target={phoneLink._target || '_self'}
                className="menu-footer-phone dniphone dniphonehref"
              >
                <span className="menu-footer-label">Get In Touch</span>
                <span className="menu-footer-number">{phoneDisplay}</span>
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          border-bottom: 1px solid transparent;
        }
        /* Reference hamburger-header spacing: ~22px/40px inset on mobile,
           25px top / 20px bottom / 50px left on desktop. */
        .header-bar-inner {
          padding-top: 1.375rem;
          padding-bottom: 1.375rem;
          padding-left: 1.25rem;
          padding-right: 1.25rem;
        }
        /* The mobile logo is absolutely centered, so the row needs its own
           height to hold it. */
        .header-bar-row {
          min-height: 4.5rem;
        }
        @media (min-width: 768px) {
          .header-bar-inner {
            padding-top: 1.5625rem;
            padding-bottom: 1.25rem;
            padding-left: 3.125rem;
            padding-right: 3.125rem;
          }
        }
        @media (min-width: 1280px) {
          .header-bar-inner {
            padding-left: 4rem;
            padding-right: 4rem;
          }
        }
        /* Reference hamburger-header logo proportions; modest bump on lg+. */
        /* Explicit height so small source files scale up; max-height alone
           never enlarges an image past its natural size. */
        .header-bar-logo {
          width: auto;
          height: 4.5rem;
          max-width: 13.5rem;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .header-bar-logo {
            height: 5rem;
            max-width: 17rem;
          }
        }
        @media (min-width: 1024px) {
          .header-bar-logo {
            height: 5.75rem;
            max-width: 20rem;
          }
        }
        .header-bar-logo-stack {
          position: relative;
        }
        /* The dark (scrolled-state) logo is often a different asset than the
           white mono logo, with its own aspect ratio. Absolutely positioning it
           at its natural size made it render smaller than the white logo, so it
           looked like the logo shrank on scroll. Instead, stretch it to fill the
           exact box the in-flow light logo establishes for the stack and let
           object-fit: contain preserve its aspect — the swap now keeps size. */
        .header-bar-logo--dark {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          max-width: none;
          max-height: none;
          object-fit: contain;
          object-position: left center;
          opacity: 0;
          pointer-events: none;
        }
        /* Logo swap on scroll. --light is the over-hero logo, --dark is the
           scrolled-bar logo React picked by luminance contrast against the
           secondary background. When no logo clears 3:1, --recolor flattens
           it to a black (light bar) or white (dark bar) silhouette. */
        .header-bar-logo {
          transition: opacity 0.3s ease, filter 0.3s ease;
        }
        .header-scrolled .header-bar-logo--light {
          opacity: 0;
        }
        .header-scrolled .header-bar-logo--dark {
          opacity: 1;
        }
        .header-scrolled.header-scrolled--on-light .header-bar-logo--recolor {
          filter: brightness(0);
        }
        .header-scrolled.header-scrolled--on-dark .header-bar-logo--recolor {
          filter: brightness(0) invert(1);
        }
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Solid background for the scrolled header. Uses the theme's
           secondary color so the header picks up the property's palette on
           scroll (previously the plain background surface / white). Kept out
           of the element's inline style so the rail-page rule below can
           drop it per breakpoint. */
        .header-scrolled {
          background-color: var(--vibe-color-secondary);
        }

        /* ---------- Scrolled-state text theming --------------------------- */
        /* Once we're over the secondary-color bar, nav / utility links,
           hamburger bars and the site-name fallback all switch to the
           luminance-picked --vantage-header-scrolled-text so they stay
           readable regardless of whether the secondary token is dark or
           light. The variable is set inline on <header> from a WCAG contrast
           pick between textOnSecondary / white / the base text color. */
        .header-scrolled .header-bar .header-nav-link,
        .header-scrolled .header-bar button.header-nav-link,
        .header-scrolled .header-bar .header-mobile-phone,
        .header-scrolled .header-bar .font-heading {
          color: var(--vantage-header-scrolled-text, var(--vibe-color-text));
        }
        /* Active nav underline follows currentColor via the base rule, so no
           extra work needed there — but the active link's text color still
           resolves to var(--vibe-color-primary), which may clash on some
           secondary hues. When the scrolled bg reads as dark we override it
           back to the picked text color so the active state stays visible
           (the underline continues to convey "active"). */
        .header-scrolled.header-scrolled--on-dark .header-bar .header-nav-link--active {
          color: var(--vantage-header-scrolled-text, #fff);
        }
        /* Hamburger bars use bg-[var(--vibe-color-text)] via inline Tailwind
           classes; mirror the transparent-header override so they flip to
           the picked color on the secondary bar. */
        .header-scrolled .header-bar span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--vantage-header-scrolled-text, var(--vibe-color-text)) !important;
        }
        /* Rail pages (the home page) on desktop: once scrolled, remove the
           white logo bar entirely — no solid background, no logo row. The
           morphing rail hamburger stays fixed in the corner as the nav access.
           We keep the <header> mounted (it still owns the drawer + menu state)
           but make it transparent and click-through, re-enabling pointer events
           on the drawer and the rail hamburger. Below md the rail is hidden,
           so the bar stays as the only nav — hence the md breakpoint guard. */
        @media (min-width: 768px) {
          .header-rail-page .header-bar {
            opacity: 1;
            visibility: visible;
            transition: opacity 0.4s ease, visibility 0s linear 0s;
          }
          .header-rail-page.header-scrolled {
            background-color: transparent;
            pointer-events: none;
          }
          .header-rail-page.header-scrolled .header-bar {
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            border-bottom-color: transparent;
            box-shadow: none;
            transition: opacity 0.4s ease, visibility 0s linear 0.4s;
          }
          .header-rail-page.header-scrolled .header-rail-hamburger {
            pointer-events: auto;
          }
          .header-rail-page.header-scrolled .mobile-menu-panel {
            pointer-events: auto;
          }
        }

        /* Click-through for the transparent header. On the home page the hero's
           action rail sits beneath the top-right of the transparent header; the
           morphing rail hamburger and the bar's own controls need to stay
           clickable. We disable pointer events on the whole transparent header,
           then re-enable them on interactive surfaces: the bar's links/buttons,
           the rail hamburger, and the entire drawer panel (which captures only
           while open — when closed it's visibility:hidden and inert regardless).
           In the scrolled / solid state this rule doesn't apply, so the bar
           captures clicks normally. pointer-events is an inherited property, so
           the panel re-enable cascades to all of its contents.

           The mobile .menu-backdrop is intentionally NOT re-enabled here: it's a
           full-screen inset-0 z-[60] element sitting above the bar, so blanket-
           enabling pointer-events would let the (invisible) closed backdrop
           swallow taps on the hamburger below it in the transparent state. Its
           open-state pointer-events are toggled directly on the element instead
           (pointer-events-auto when open, pointer-events-none when closed). */
        .header-transparent {
          pointer-events: none;
        }
        .header-transparent .header-bar a,
        .header-transparent .header-bar button,
        .header-transparent .header-rail-hamburger,
        .header-transparent .mobile-menu-panel {
          pointer-events: auto;
        }

        /* ================================================================ */
        /* Desktop horizontal navigation (lg+)                             */
        /* ================================================================ */
        .header-desktop-nav {
          gap: 0.15rem;
        }
        .header-nav-link {
          position: relative;
          display: inline-flex;
          align-items: center;
          padding: 0.5rem 0.8rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: var(--vibe-color-text);
          text-decoration: none;
          white-space: nowrap;
          transition: color 0.2s ease;
        }
        button.header-nav-link {
          border: 0;
          cursor: pointer;
          appearance: none;
        }
        .header-nav-link--active {
          color: var(--vibe-color-primary);
        }
        /* Active-page underline (uses currentColor so it stays white over the
           hero and switches to the theme color on the scrolled/solid bar). */
        .header-desktop-nav .header-nav-link--active::after {
          content: '';
          position: absolute;
          left: 0.8rem;
          right: 0.8rem;
          bottom: 0.15rem;
          height: 2px;
          border-radius: 2px;
          background: currentColor;
        }
        /* Hover / active dropdown for parent nav items (white surface). */
        .header-nav-group {
          display: inline-flex;
        }
        .nav-more-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          min-width: 12rem;
          padding: 0.4rem 0;
          background: var(--vibe-color-background);
          border: 1px solid var(--vibe-color-border);
          border-radius: 2px;
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
          opacity: 0;
          visibility: hidden;
          transform: translateY(6px);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0.2s;
          z-index: 60;
        }
        .header-nav-group:hover .nav-more-dropdown,
        .header-nav-group:focus-within .nav-more-dropdown {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0s linear 0s;
        }
        .nav-more-dropdown-link {
          display: block;
          padding: 0.5rem 1.1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.15s ease;
        }
        /* Don't use --vibe-color-surface here: property_color_3 maps to
           surface and is often a dark brand hue, which yields black text on
           a dark row. Tint the (light) panel background with primary instead. */
        .nav-more-dropdown-link:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 14%, var(--vibe-color-background));
        }

        /* Schedule a Tour — primary CTA at the far right of the nav row
           (md+). Flat "comfy-cta" shape: solid primary fill, sharp corners,
           no border. */
        .header-tour-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.65rem 1.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
          text-decoration: none;
          white-space: nowrap;
          transition: background-color 0.3s ease, color 0.3s ease;
        }
        .header-tour-cta:hover {
          background: var(--vibe-color-background);
          color: var(--vibe-color-text);
        }

        /* ================================================================ */
        /* Morphing rail hamburger (desktop home)                          */
        /* ================================================================ */
        .header-rail-hamburger {
          position: fixed;
          right: 0;
          z-index: 55;
          top: var(--rail-hamburger-top, var(--offer-banner-height, 0px));
          width: var(--rail-hamburger-width, 168px);
          min-width: var(--rail-hamburger-width, 168px);
          max-width: var(--rail-hamburger-width, 168px);
          height: var(--rail-hamburger-height, 88px);
          min-height: var(--rail-hamburger-height, 88px);
          max-height: var(--rail-hamburger-height, 88px);
          align-items: center;
          justify-content: center;
          border: none;
          cursor: pointer;
          color: var(--vibe-color-text-on-primary, #fff);
          background: color-mix(in srgb, var(--vibe-color-primary) 82%, transparent);
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          transition: background 0.3s ease;
        }
        .header-rail-hamburger:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 95%, transparent);
        }
        .header-rail-hamburger:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #fff);
          outline-offset: -3px;
        }
        .header-rail-bars {
          position: relative;
          width: 26px;
          height: 16px;
        }
        .header-rail-bars span {
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          border-radius: 2px;
          background: currentColor;
          transition: transform 0.3s ease, opacity 0.2s ease, top 0.3s ease;
        }
        .header-rail-bars span:nth-child(1) { top: 0; }
        .header-rail-bars span:nth-child(2) { top: 7px; }
        .header-rail-bars span:nth-child(3) { top: 14px; }
        .header-rail-hamburger[aria-expanded="true"] .header-rail-bars span:nth-child(1) {
          top: 7px;
          transform: rotate(45deg);
        }
        .header-rail-hamburger[aria-expanded="true"] .header-rail-bars span:nth-child(2) {
          opacity: 0;
        }
        .header-rail-hamburger[aria-expanded="true"] .header-rail-bars span:nth-child(3) {
          top: 7px;
          transform: rotate(-45deg);
        }

        /* Transparent header: nav links → white (header bar only — NOT inside the mobile menu panel) */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar nav button.header-nav-link,
        .header-transparent .header-bar button.font-body {
          color: rgba(255,255,255,0.92) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar nav button.header-nav-link:hover,
        .header-transparent .header-bar button.font-body:hover {
          color: #fff !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar nav button.header-nav-link,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--vantage-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar nav button.header-nav-link:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover {
          color: var(--vantage-nav-over-hero-text, #fff) !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. The dropdown is a white-background
           context, so the hover affordance is the animated underline from
           .vantage-nav-darken-hover below — we keep the text color steady
           on hover (still !important to override the parent
           .header-transparent .header-bar nav a:hover white rule).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Mobile call icon (right of the centered logo). */
        .header-mobile-phone {
          color: var(--vibe-color-text);
        }
        .header-transparent .header-mobile-phone {
          color: #fff;
        }
        .header-transparent.header-over-solid-hero .header-mobile-phone {
          color: var(--vantage-nav-over-hero-text, #fff);
        }

        /* Off-canvas drawer (slides in from the left): half-width sheet on
           tablet; on mobile the panel leaves an exposed strip so visitors can
           tap the dimmed backdrop outside to dismiss. */
        .mobile-menu-panel {
          background-color: #0b0c10;
        }
        .mobile-menu-panel.mobile-menu-open {
          box-shadow: 16px 0 50px rgba(0, 0, 0, 0.4);
        }
        .mobile-menu-panel .menu-drawer-info {
          margin-top: clamp(1.25rem, 3vh, 2rem);
          max-width: 22rem;
        }
        .mobile-menu-panel .menu-drawer-heading {
          margin-bottom: 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: var(--vantage-drawer-text, #fff);
        }
        .mobile-menu-panel .menu-drawer-info-text,
        .mobile-menu-panel .menu-drawer-hours {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--vantage-drawer-text, #fff);
        }
        .mobile-menu-panel .menu-drawer-info-link {
          display: block;
          text-decoration: none;
        }
        .mobile-menu-panel .menu-drawer-info-link:hover {
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .mobile-menu-panel .menu-drawer-hours {
          display: grid;
          grid-template-columns: auto auto;
          justify-content: start;
          column-gap: 1.5rem;
          row-gap: 0.25rem;
        }
        .mobile-menu-panel .menu-drawer-hours dd {
          margin: 0;
          white-space: nowrap;
        }
        .mobile-menu-panel .menu-drawer-hours dt {
          white-space: nowrap;
        }
        @media (min-width: 1024px) {
          .mobile-menu-panel.mobile-menu-open {
            box-shadow: none;
          }
        }
        .menu-backdrop {
          cursor: pointer;
        }
        .menu-panel-header {
          flex-shrink: 0;
        }
        .menu-layout {
          width: 100%;
        }
        .menu-divider {
          width: 1px;
          align-self: stretch;
          background: rgba(255, 255, 255, 0.35);
          margin: 0 2.5rem;
        }
        .menu-nav-link {
          color: var(--vantage-drawer-text, #fff);
        }
        .menu-nav-link:hover {
          color: var(--vantage-drawer-text, #fff);
        }
        .menu-nav-link--active {
          color: var(--vibe-color-secondary, #7ec8e3);
        }
        .menu-contact-row {
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .menu-contact-row:hover {
          color: #fff;
        }
        .menu-contact-icon-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          flex-shrink: 0;
          color: #fff;
        }
        .menu-contact-icon-badge svg {
          width: 1.125rem;
          height: 1.125rem;
        }
        .menu-contact-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.45;
        }
        @media (min-width: 768px) {
          .menu-contact-text {
            font-size: 1.125rem;
          }
        }
        .menu-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          color: var(--vibe-color-primary);
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .menu-social-link:hover {
          color: #fff;
          transform: translateY(-1px);
        }
        .menu-gallery-carousel {
          position: relative;
          width: 100%;
          max-width: 28rem;
          aspect-ratio: 4 / 3;
          margin-top: 2rem;
          overflow: hidden;
          border-radius: 2px;
        }
        .menu-gallery-carousel__slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.8s ease;
        }
        .menu-gallery-carousel__slide.is-active {
          opacity: 1;
        }
        .menu-gallery-carousel__image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        /* Backdrop image + black semi-transparent wash. The image fills the
           panel and the wash darkens it so the light content reads cleanly. */
        .mobile-menu-panel .menu-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .mobile-menu-panel .menu-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: rgba(0, 0, 0, 0.82);
        }
        /* Close button — top-right of the panel header. */
        .mobile-menu-panel .menu-close-btn {
          position: static;
          width: 2.75rem;
          height: 2.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--vantage-drawer-text, #fff);
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .mobile-menu-panel .menu-close-btn:hover {
          color: var(--vantage-drawer-text, #fff);
          transform: rotate(90deg);
        }
        .mobile-menu-panel .menu-close-btn svg {
          width: 1.75rem;
          height: 1.75rem;
        }

        /* Dark drawer: content renders in light text over the dimmed image. */
        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: var(--vantage-drawer-text, #fff);
        }
        .mobile-menu-panel a:hover {
          color: var(--vantage-drawer-text, #fff);
        }
        /* Row dividers on the dark surface — nav links no longer use borders. */
        .mobile-menu-panel nav a {
          border-bottom-color: transparent;
        }

        /* Nav links: animated underline on hover (used by Header
           DesktopNavLink, "More" dropdown items, MobileNavLink, and the
           Navigation sub-bar). All of these contexts render against a
           white surface, so the affordance is an underline rather than a
           color shift — the underline is drawn via text-decoration so it
           rides the text baseline and works at any link padding. The
           transparent-header (over-hero) brightening hover above keeps
           its own color shift via earlier !important rules; the
           underline simply layers on top with currentColor. */
        .vantage-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .vantage-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Address / phone rows in the mobile drawer wear the
           vantage-nav-darken-hover class on the inner text span (not the link
           itself, so the underline doesn't run under the icon). Trigger
           the underline from the parent link's :hover/:focus-visible so
           the whole row remains the hover target. */
        .mobile-menu-panel a:hover .vantage-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .vantage-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Outline pills inside the drawer flip to text-on-primary on hover. */
        .mobile-menu-panel .mobile-util-pill:hover {
          color: var(--vibe-color-text-on-primary);
        }

        /* Transparent header: hamburger bars → white */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }

        /* Transparent header: site name fallback text */
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* Interior pages: hamburger bars + site-name fallback also flip to
           the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--vantage-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--vantage-nav-over-hero-text, #fff) !important;
        }

        /* ================================================================ */
        /* Utility row (lg+) — solid primary strip above the nav row, the  */
        /* same in both the transparent and scrolled header states. Text   */
        /* uses --vantage-util-scrolled-text, the contrast pick against    */
        /* the primary color, so it reads on any palette.                  */
        /* ================================================================ */
        .header-util-row {
          background-color: var(--vibe-color-primary);
          color: var(--vantage-util-scrolled-text, var(--vibe-color-text-on-primary));
        }
        .header-util-row-inner {
          height: 2.5rem;
          padding-left: 3.125rem;
          padding-right: 3.125rem;
        }
        @media (min-width: 1280px) {
          .header-util-row-inner {
            padding-left: 4rem;
            padding-right: 4rem;
          }
        }
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          min-width: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: inherit;
          text-decoration: underline;
          text-decoration-color: transparent;
          text-underline-offset: 4px;
          white-space: nowrap;
          transition: text-decoration-color 0.2s ease, opacity 0.2s ease;
        }
        .header-util-link:hover {
          text-decoration-color: currentColor;
        }
        .header-util-address {
          max-width: 28rem;
          text-transform: none;
          letter-spacing: 0.02em;
        }

        /* ================================================================ */
        /* CTA Button — transparent / outline style                        */
        /* ----------------------------------------------------------------- */
        /* Border + label use --vantage-nav-cta-accent (luminance-picked).
           That variable resolves to --vibe-color-primary when the property's
           primary has WCAG AA contrast against the white scrolled header,
           and falls back to --vibe-color-text (the dark theme color) for
           pale primaries that would otherwise wash out on white. The hover
           fill follows the same accent so the swap is consistent. */
        .nav-cta-btn {
          background: transparent;
          border: 1.5px solid var(--vantage-nav-cta-accent, var(--vibe-color-primary));
          border-radius: 2px;
          color: var(--vantage-nav-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vantage-nav-cta-accent, var(--vibe-color-primary));
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .nav-cta-btn:hover {
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vantage-nav-cta-accent, var(--vibe-color-primary)) 25%, transparent);
          transform: translateY(-1px);
          color: var(--vantage-nav-cta-on-accent, var(--vibe-color-text-on-primary));
        }
        .nav-cta-btn:hover::before {
          opacity: 1;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* When header is transparent (over hero) — white border & text */
        .header-transparent .nav-cta-btn {
          border-color: rgba(255,255,255,0.75);
          color: #fff;
        }
        .header-transparent .nav-cta-btn::before {
          background: rgba(255,255,255,0.18);
        }
        .header-transparent .nav-cta-btn:hover {
          border-color: #fff;
          color: #fff;
          box-shadow: none;
        }

        /* Interior pages: CTA border / text follow the luminance-picked
           on-color token. The translucent fill that slides in on hover uses
           color-mix() so it stays in palette when the picked color is dark. */
        .header-transparent.header-over-solid-hero .nav-cta-btn {
          border-color: color-mix(in srgb, var(--vantage-nav-over-hero-text, #fff) 75%, transparent);
          color: var(--vantage-nav-over-hero-text, #fff);
        }
        .header-transparent.header-over-solid-hero .nav-cta-btn::before {
          background: color-mix(in srgb, var(--vantage-nav-over-hero-text, #fff) 18%, transparent);
        }
        .header-transparent.header-over-solid-hero .nav-cta-btn:hover {
          border-color: var(--vantage-nav-over-hero-text, #fff);
          color: var(--vantage-nav-over-hero-text, #fff);
          box-shadow: none;
        }

        /* ================================================================ */
        /* Drawer treatment (all widths): solid surface, single-column nav, */
        /* filled CTAs, and a "Get In Touch" contact footer band. Mobile    */
        /* now shares the tablet look — the photographic-backdrop sheet      */
        /* above is fully overridden here. min-width:0 keeps this as one     */
        /* always-on block (was md+; lowered so <768px matches the tablet). */
        /* ================================================================ */
        @media (min-width: 0px) {
          /* Solid secondary fill; drop the photo backdrop + wash. Text on
             this surface uses --vantage-drawer-text (luminance-picked). */
          .mobile-menu-panel {
            background-color: var(--vantage-drawer-bg, var(--vibe-color-secondary));
            color: var(--vantage-drawer-text, var(--vibe-color-text-on-secondary));
          }
          header .mobile-menu-panel .menu-logo-fallback.font-heading {
            color: var(--vantage-drawer-text, #fff) !important;
          }
          .mobile-menu-panel .menu-bg,
          .mobile-menu-panel .menu-overlay {
            display: none;
          }

          /* Single column: hide the right-hand contact / gallery column. */
          .mobile-menu-panel .menu-contact,
          .mobile-menu-panel .menu-divider {
            display: none;
          }
          .mobile-menu-panel .menu-nav {
            max-width: none;
            flex: 1 1 auto;
          }

          /* Single-column drawer: drop the two-column min-height so nav links
             and CTAs size naturally and stay above the pinned footer band
             instead of overflowing behind it. Tighter top padding claws back
             vertical space so the CTAs fit without scrolling. */
          .mobile-menu-panel .menu-layout {
            min-height: 0;
            padding-top: 1.5rem;
          }

          /* Keep the scroll region shrinkable (min-height: 0) and internally
             scrollable so tall nav content never pushes the pinned "Get In
             Touch" footer band — and its phone number — past the panel's
             overflow-hidden edge, where it would be clipped from view.
             Drop the base bottom padding here: on tablet+ the pinned footer
             band supplies the bottom spacing, so this padding was just dead
             space that forced a scrollbar. With it gone the drawer fits
             without scrolling on normal viewports; the scroll only kicks in
             as a fallback on genuinely short screens. */
          .mobile-menu-panel .menu-scroll {
            overflow-y: auto;
            min-height: 0;
            padding-bottom: 0;
          }

          /* Larger nav labels to match the reference, but scaled to viewport
             height: on tall screens they stay at the full 1.5rem, and they
             shrink automatically on short screens so the CTAs and the pinned
             "Get In Touch" band fit without a scrollbar. */
          .mobile-menu-panel .menu-nav-link {
            font-size: clamp(1.2rem, 2.6vh, 1.5rem);
            padding-top: clamp(0.3rem, 1.1vh, 0.5rem);
            padding-bottom: clamp(0.3rem, 1.1vh, 0.5rem);
          }
          /* Nested pages: indented under their parent / group heading with
             a thin guide rule, one step smaller than top-level rows. */
          .mobile-menu-panel .menu-nav-children {
            margin: 0 0 clamp(0.25rem, 0.8vh, 0.5rem) 0.25rem;
            padding-left: 1rem;
            border-left: 1px solid color-mix(in srgb, var(--vantage-drawer-text, #fff) 45%, transparent);
          }
          .mobile-menu-panel .menu-nav-link--child {
            font-size: clamp(1rem, 2.1vh, 1.2rem);
            padding-top: clamp(0.2rem, 0.8vh, 0.35rem);
            padding-bottom: clamp(0.2rem, 0.8vh, 0.35rem);
            color: var(--vantage-drawer-text, #fff);
          }
          /* Active item: drawer text with a riding underline. */
          .mobile-menu-panel .menu-nav-link--active {
            color: var(--vantage-drawer-text, #fff);
            text-decoration: underline;
            text-decoration-thickness: 2px;
            text-underline-offset: 10px;
          }

          /* Resident Access / Apply Now / Schedule a Tour → filled primary buttons, stacked
             under each other. */
          .mobile-menu-panel .menu-drawer-cta-row {
            display: flex;
            flex-direction: column;
            gap: clamp(0.5rem, 1.4vh, 0.85rem);
            margin-top: clamp(0.75rem, 2vh, 1.25rem);
            max-width: 22rem;
          }

          /* Listed after the .menu-nav-link rule so its font-size wins on
             equal specificity. */
          .mobile-menu-panel .menu-drawer-cta {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            padding: 1rem 1.5rem;
            font-size: 1.05rem;
            font-weight: 700;
            letter-spacing: 0.01em;
            text-transform: none;
            background: var(--vantage-drawer-cta-bg, var(--vibe-color-primary));
            color: var(--vantage-drawer-cta-text, var(--vibe-color-text-on-primary));
            text-decoration: none;
            transition: background-color 0.25s ease, color 0.25s ease;
          }
          .mobile-menu-panel .menu-drawer-cta:hover {
            background: color-mix(in srgb, var(--vantage-drawer-cta-bg, var(--vibe-color-primary)) 88%, black);
            color: var(--vantage-drawer-cta-text, var(--vibe-color-text-on-primary));
          }
          .mobile-menu-panel .menu-drawer-cta:focus-visible,
          .mobile-menu-panel .menu-nav-link:focus-visible,
          .mobile-menu-panel .menu-close-btn:focus-visible {
            outline: 2px solid var(--vantage-drawer-text, #fff);
            outline-offset: 3px;
          }

          /* Contact footer band — full-width strip pinned to the panel base. */
          .mobile-menu-panel .menu-footer-band {
            flex-shrink: 0;
            margin-top: 1rem;
            padding: 1.25rem 2.5rem 1.5rem;
            background: var(--vantage-drawer-band-bg, var(--vibe-color-primary));
          }
          .mobile-menu-panel .menu-footer-phone {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            text-decoration: none;
            color: var(--vantage-drawer-band-text, #fff);
          }
          .mobile-menu-panel .menu-footer-phone:hover {
            color: var(--vantage-drawer-band-text, #fff);
          }
          .mobile-menu-panel .menu-footer-phone:focus-visible {
            outline: 2px solid var(--vantage-drawer-band-text, #fff);
            outline-offset: 3px;
          }
          .mobile-menu-panel .menu-footer-label {
            font-family: var(--vibe-font-body);
            font-size: 1.05rem;
            font-weight: 700;
            color: var(--vantage-drawer-band-text, #fff);
          }
          .mobile-menu-panel .menu-footer-number {
            font-family: var(--vibe-font-heading);
            font-size: 1.4rem;
            font-weight: 800;
            line-height: 1.1;
            color: var(--vantage-drawer-band-text, #fff);
          }
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {},
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        text: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: { label: 'URL', type: 'url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.phone_link': {
      label: 'Phone Link',
      fields: {
        text: {
          label: 'Phone Text',
          type: 'text',
          defaultField: 'property_call_tracking_phone',
        },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.text_us_link': {
      label: 'Text Us Link',
      fields: {
        text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.facebook_link': {
      label: 'Facebook',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_facebook_url',
          suggestedFields: ['property_facebook_url'],
        },
        text: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.instagram_link': {
      label: 'Instagram',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_instagram_url',
          suggestedFields: ['property_instagram_url'],
        },
        text: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
    'header.menu_gallery': {
      label: 'Menu Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 5,
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
